import { useState, useRef, useEffect } from "react";
import { ArrowDown, Copy, Check, ChevronUp, AlertTriangle } from "lucide-react";

// ─── Format seconds → MM:SS timestamp ────────────────────────────────────────
function formatTimestamp(seconds) {
  if (seconds == null || seconds < 0) return "";
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

// ─── Format utterances for clipboard (matches the displayed layout) ──────────
function formatForCopy(utterances, fallbackText) {
  const cleaned = utterances.filter(u => u.status === "cleaned");
  if (cleaned.length > 0) {
    return cleaned.map(u => {
      const ts = formatTimestamp(u.start_time);
      return ts ? `${ts} ·\n${u.text}` : u.text;
    }).join("\n\n");
  }
  return fallbackText || "";
}

// ─── Single utterance row ─────────────────────────────────────────────────────
function UtteranceItem({ utterance, isEditable, onTextChange }) {
  const isInterim = utterance.status === "interim";

  if (isInterim) {
    return (
      <div
        className="mb-3 text-sm italic text-slate-400 leading-snug"
        style={{ transition: "opacity 0.4s ease", opacity: 0.65 }}
      >
        {utterance.text}
      </div>
    );
  }

  const timestamp = formatTimestamp(utterance.start_time);

  return (
    <div className="mb-3" style={{ transition: "opacity 0.4s ease", opacity: 1 }}>
      {/* Timestamp label row — only shown when we have a non-zero start_time */}
      {timestamp && (
        <div className="flex items-center gap-1 mb-0.5 text-slate-500">
          <span className="text-[11px] font-bold tracking-widest">
            {timestamp}
          </span>
          <span className="font-black text-sm select-none leading-none"> ·</span>
        </div>
      )}

      {/* Text row — editable after recording stops */}
      <div
        className="text-sm text-gray-900 leading-snug focus:outline-none"
        contentEditable={isEditable}
        suppressContentEditableWarning
        onBlur={e => {
          if (isEditable && onTextChange) {
            onTextChange(utterance.id, e.currentTarget.innerText.trim());
          }
        }}
        style={{ outline: "none" }}
      >
        {utterance.text}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export const TranscriptContainer = ({
  isVisible,
  className,
  isRecording,
  showRecommendation,
  setIsTranscriptEdited,
  transcriptScrollRef,
  editableTranscriptionRef,
  transcribedText,
  recommendationLoading,
  recommendation,
  isTranscriptEdited,
  createSessionLoading,
  loading,
  handleTranscriptUpdate,
  StarsIcon,
  Spinner,
  WarnIcon,
  autoScrollEnabledRef,
  setShowScrollButton,
  showScrollButton,
  TypewriterMarkdown,
  typedRecommendationRef,
  liveUtterances = [],
  onUtteranceTextChange,
  shortTranscriptWarning = false,
}) => {
  const [copied, setCopied] = useState(false);
  const [copyMenuOpen, setCopyMenuOpen] = useState(false);
  const [copyMode, setCopyMode] = useState("timestamps"); // "timestamps" | "no-timestamps"
  const [transcriptFocused, setTranscriptFocused] = useState(false);
  const copyMenuRef = useRef(null);

  // Close the dropdown when the user clicks outside it
  useEffect(() => {
    const handler = (e) => {
      if (copyMenuRef.current && !copyMenuRef.current.contains(e.target)) {
        setCopyMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const flashCopied = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Default — copy with "MM:SS ·\ntext" format, blocks separated by blank lines
  const handleCopyWithTimestamps = async () => {
    const text = formatForCopy(
      liveUtterances,
      editableTranscriptionRef.current || transcribedText
    );
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      flashCopied();
    } catch { }
    setCopyMode("timestamps");
    setCopyMenuOpen(false);
  };

  // Copy without timestamps — each timestamp block becomes its own paragraph
  const handleCopyWithoutTimestamps = async () => {
    const cleaned = liveUtterances.filter(u => u.status === "cleaned");
    let text;
    if (cleaned.length > 0) {
      text = cleaned.map(u => u.text).join("\n\n");
    } else {
      text = editableTranscriptionRef.current || transcribedText || "";
    }
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      flashCopied();
    } catch { }
    setCopyMode("no-timestamps");
    setCopyMenuOpen(false);
  };

  // Sort cleaned utterances by start_time (concurrent GPT flushes can arrive out of order)
  // Keep interims at the end so the live rolling text always appears below the cleaned blocks
  const displayUtterances = [
    ...[...liveUtterances]
      .filter(u => u.status === "cleaned")
      .sort((a, b) => (a.start_time ?? 0) - (b.start_time ?? 0)),
    ...liveUtterances.filter(u => u.status === "interim"),
  ];

  const hasLiveContent = liveUtterances.length > 0;
  const hasTranscript =
    transcribedText ||
    (editableTranscriptionRef.current && editableTranscriptionRef.current.length > 0) ||
    hasLiveContent;

  return (
    <div
      className={`relative top-0 right-0 xl:h-full transform transition-transform duration-300 ease-in-out
        ${isVisible ? "translate-x-0" : "hidden translate-x-full"}
        ${className}`}
      onClick={() => {
        // Only trigger legacy edit mode when showing flat text, not the utterance list
        if (isRecording || showRecommendation || hasLiveContent) return;
        setIsTranscriptEdited(true);
      }}
    >
      <div
        className={`rounded-[19px] group relative bg-white pl-7 pt-7 border-2 shadow-lg border-gray-300 w-full h-96 md:h-[calc(65vh-20px)] flex flex-col
          ${showRecommendation ? "pb-12" : "pb-16"}`}
      >
        <div
          className="flex-1 pr-7 overflow-y-auto transcript-scroll relative [scrollbar-gutter:stable]"
          ref={transcriptScrollRef}
          onScroll={() => {
            const el = transcriptScrollRef.current;
            if (!el) return;
            const { scrollTop, scrollHeight, clientHeight } = el;
            const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
            autoScrollEnabledRef.current = isAtBottom;
            setShowScrollButton(!isAtBottom && isRecording);
          }}
        >
          {/* ── Recommendation view ──────────────────────────────────────── */}
          {showRecommendation && (
            <div className="text-sm outline-none min-h-[20px]">
              {recommendationLoading
                ? "Analysing your session..."
                : recommendation.length > 0
                ? (
                  <TypewriterMarkdown
                    text={recommendation}
                    speed={2}
                    startDelay={400}
                    uniqueId="recommendation"
                    typedNotesRef={typedRecommendationRef}
                  />
                )
                : "Insights will appear here..."}
            </div>
          )}

          {/* ── Ambient utterance list (live + persists after recording) ─── */}
          {!showRecommendation && hasLiveContent && (
            <div className="text-sm">
              {displayUtterances.map(u => (
                <UtteranceItem
                  key={u.id}
                  utterance={u}
                  isEditable={!isRecording}
                  onTextChange={onUtteranceTextChange}
                />
              ))}
              {isRecording && (
                <div className="text-[11px] text-slate-400 italic animate-pulse mt-1">
                  Listening...
                </div>
              )}
            </div>
          )}

          {/* ── Listening placeholder (recording, no utterances yet) ───── */}
          {!showRecommendation && !hasLiveContent && isRecording && (
            <div className="absolute top-0 left-0 text-sm text-slate-400 pointer-events-none italic">
              Listening...
            </div>
          )}

          {/* ── Flat editable transcript (no live utterances — saved sessions) ── */}
          {!showRecommendation && !hasLiveContent && !isRecording && (
            <>
              {!hasTranscript && !transcriptFocused && (
                <div className="absolute top-0 left-0 text-sm text-gray-900 pointer-events-none">
                  Your transcript will appear here...
                </div>
              )}
              <div
                className="text-sm outline-none min-h-[20px]"
                contentEditable={true}
                suppressContentEditableWarning
                onInput={e => {
                  editableTranscriptionRef.current = e.currentTarget.innerText;
                }}
                onFocus={() => setTranscriptFocused(true)}
                onBlur={() => setTranscriptFocused(false)}
              >
                {editableTranscriptionRef.current.length > transcribedText.length
                  ? editableTranscriptionRef.current
                  : transcribedText}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Short transcript warning ─────────────────────────────────── */}
      {!showRecommendation && shortTranscriptWarning && (
        <div className="absolute bottom-4 left-[26px] z-[99] flex items-center gap-2 bg-amber-50 border border-amber-300 rounded-xl px-3 py-[7px] text-xs text-amber-700">
          <AlertTriangle size={13} className="shrink-0 text-amber-500" />
          <span>Short transcript — review notes carefully</span>
        </div>
      )}

      {/* ── Bottom action buttons: Update + Copy ─────────────────────── */}
      {!showRecommendation && (
        <div className="absolute bottom-4 right-[26px] z-[99] flex items-center gap-2">
          {isTranscriptEdited && (
            <div
              className="cursor-pointer select-none flex items-center gap-1 py-[5px] px-[10px] bg-black rounded-2xl font-medium text-white text-sm"
              contentEditable={false}
              onMouseDown={() => {
                if (createSessionLoading || loading) return;
                handleTranscriptUpdate();
              }}
            >
              <StarsIcon />
              {createSessionLoading || loading ? <Spinner /> : "Update"}
            </div>
          )}
          {(hasLiveContent || hasTranscript) && (
            <div ref={copyMenuRef} className="relative">
              {/* Dropdown — opens upward above the button */}
              {copyMenuOpen && (
                <div className="absolute bottom-full right-0 mb-1.5 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-20 min-w-[200px]">
                  <button
                    onClick={handleCopyWithTimestamps}
                    className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-gray-800 hover:bg-gray-50 transition-colors text-left"
                  >
                    {/* Tick takes the space of the copy icon so text stays aligned */}
                    {copyMode === "timestamps"
                      ? <Check size={13} strokeWidth={2.5} className="shrink-0 text-gray-800" />
                      : <span className="w-[13px] shrink-0" />
                    }
                    Copy with timestamps
                  </button>
                  <div className="border-t border-gray-100" />
                  <button
                    onClick={handleCopyWithoutTimestamps}
                    className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-gray-800 hover:bg-gray-50 transition-colors text-left"
                  >
                    {copyMode === "no-timestamps"
                      ? <Check size={13} strokeWidth={2.5} className="shrink-0 text-gray-800" />
                      : <span className="w-[13px] shrink-0" />
                    }
                    Copy without timestamps
                  </button>
                </div>
              )}

              {/* Split button: left = copy with timestamps (default), right = open menu */}
              <div className="flex items-stretch">
                <button
                  onClick={handleCopyWithTimestamps}
                  className="flex items-center gap-1.5 py-[5px] pl-[10px] pr-2.5 bg-primary hover:bg-primary/80 rounded-l-2xl font-medium text-black text-sm transition-colors duration-150 select-none"
                >
                  {copied
                    ? <><Check size={13} strokeWidth={2.5} /><span>Copied</span></>
                    : <><Copy size={13} strokeWidth={2} /><span>Copy</span></>
                  }
                </button>
                <button
                  onClick={() => setCopyMenuOpen(o => !o)}
                  className="flex items-center justify-center py-[5px] px-1.5 bg-primary hover:bg-primary/80 rounded-r-2xl text-black transition-colors duration-150 border-l border-teal-400 select-none"
                  aria-label="More copy options"
                >
                  <ChevronUp size={12} strokeWidth={2.5} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── AI advisory disclaimer ───────────────────────────────────────── */}
      {showRecommendation && (
        <div className="absolute bottom-2 left-6 text-xs flex items-center gap-3 font-semibold">
          <div>
            <img src={WarnIcon} alt="" width={18} />
          </div>
          <div className="italic font-normal">
            AI-generated insights are advisory only and must be interpreted using clinical judgment
          </div>
        </div>
      )}

      {/* ── Scroll-to-bottom button ──────────────────────────────────────── */}
      {showScrollButton && (
        <button
          onClick={() => {
            const el = transcriptScrollRef.current;
            if (!el) return;
            el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
            autoScrollEnabledRef.current = true;
            setShowScrollButton(false);
          }}
          className="absolute bottom-16 left-1/2 -translate-x-1/2
            bg-blue4 text-black font-bold px-3 py-1.5 rounded-full text-xs
            flex items-center gap-1 shadow-lg z-10"
        >
          <ArrowDown size={14} />
          New Transcript
        </button>
      )}
    </div>
  );
};
